/**
 * Income, resolved onto all three axes.
 *
 * The old model stored one blended monthly figure per side — `Net` and
 * `Gross`, each `annual / 12`. That is a perfectly good accrual number and it
 * is the right CSP denominator, but it has two problems the moment more than
 * one job exists:
 *
 *  1. It cannot be decomposed. Two jobs on different cadences blend into one
 *     average with no way back out, so nothing downstream can know that a line
 *     funded off the 26-schedule is covered differently from one funded off
 *     the 24.
 *  2. It is not observable. A pay stub states a per-check figure; the monthly
 *     one is derived from it, never the other way round. Storing only the
 *     derived value means every correction has to be done by hand, in Notion,
 *     against arithmetic nobody re-checks.
 *
 * So per-check plus a schedule is the source of truth where it exists, and the
 * monthly figure is the fallback for a row that predates the column. Both
 * paths land on `annual`, which is the axis all three views agree on.
 */

import type { FundingSchedule, IncomeStream, IncomeStreams } from './types.js';
import {
  checkToAnnual, checksInMonth, guaranteedChecksPerMonth, indexSchedules, scheduleFor,
} from './calendar.js';

/**
 * Which side of the tax line a row describes.
 *
 * Matched on the name because that is what Notion has: the table is two rows
 * called `Net` and `Gross`, and per-job rows would most naturally be called
 * `Paradigm Net` / `Paradigm Gross`. Anything unrecognised counts as net —
 * take-home is the axis the budget is drawn from, so an unlabelled income row
 * is far more likely to be that than gross, and mis-classifying it as gross
 * would quietly remove it from every figure that matters.
 */
export const isGrossStream = (name: string): boolean => /\bgross\b/i.test(name ?? '');

/** Annual value of one stream, preferring the observable per-check figure. */
export function annualOf(
  s: IncomeStream,
  schedules: FundingSchedule[] = [],
): number {
  if (!s.isActive) return 0;
  const index = indexSchedules(schedules);
  const sched = scheduleFor(index, s.fundingScheduleId, schedules);
  if (s.perCheck > 0) return checkToAnnual(s.perCheck, sched);
  // No per-check figure yet — Notion's accrual monthly is the best available.
  return s.monthlyAmount * 12;
}

/**
 * Per-check value of one stream.
 *
 * The inverse of `annualOf`: given only a monthly figure, divide the annual it
 * implies by the schedule's own count. That reconstruction is exact — it is
 * the same identity read the other way — but it can only run once the row has
 * a schedule. Without one there is no cadence to divide by and the honest
 * answer is zero rather than a number derived from a guessed divisor.
 */
export function perCheckOf(
  s: IncomeStream,
  schedules: FundingSchedule[] = [],
): number {
  if (!s.isActive) return 0;
  if (s.perCheck > 0) return s.perCheck;
  const sched = scheduleFor(indexSchedules(schedules), s.fundingScheduleId, schedules);
  if (!sched) return 0;
  return (s.monthlyAmount * 12) / sched.paycheckCount;
}

/** Roll the raw rows up onto all three axes. */
export function deriveIncome(
  streams: IncomeStream[] = [],
  schedules: FundingSchedule[] = [],
): IncomeStreams {
  const index = indexSchedules(schedules);
  const active = (streams ?? []).filter(s => s.isActive);

  let netAnnual = 0;
  let grossAnnual = 0;
  let netGuaranteedMonthly = 0;
  let grossGuaranteedMonthly = 0;

  for (const s of active) {
    const annual = annualOf(s, schedules);
    const sched = scheduleFor(index, s.fundingScheduleId, schedules);
    const perCheck = perCheckOf(s, schedules);
    // A row with no resolvable schedule has no guaranteed figure of its own —
    // fall back to its accrual twelfth rather than dropping it, so the total
    // never silently loses a stream.
    const guaranteed = perCheck > 0 && sched
      ? perCheck * guaranteedChecksPerMonth(sched)
      : annual / 12;

    if (isGrossStream(s.name)) {
      grossAnnual += annual;
      grossGuaranteedMonthly += guaranteed;
    } else {
      netAnnual += annual;
      netGuaranteedMonthly += guaranteed;
    }
  }

  return {
    netMonthly: netAnnual / 12,
    grossMonthly: grossAnnual / 12,
    netGuaranteedMonthly,
    grossGuaranteedMonthly,
    netAnnual,
    grossAnnual,
    sources: streams ?? [],
  };
}

/**
 * Net income landing in one specific calendar month.
 *
 * This is the figure the projection pays out. Ten bi-weekly months hold two
 * checks and two hold three, and smearing that into twelve equal twelfths is
 * what let a plan look solvent through a stretch it would not survive.
 */
export function netInMonth(
  income: IncomeStreams,
  schedules: FundingSchedule[],
  year: number,
  month: number,
): number {
  const index = indexSchedules(schedules);
  let total = 0;

  for (const s of income.sources) {
    if (!s.isActive || isGrossStream(s.name)) continue;
    const sched = scheduleFor(index, s.fundingScheduleId, schedules);
    const perCheck = perCheckOf(s, schedules);
    if (perCheck > 0 && sched) total += perCheck * checksInMonth(sched, year, month);
    else total += (annualOf(s, schedules)) / 12; // no calendar available
  }
  return total;
}

/**
 * Does this income model know its own pay calendar?
 *
 * False means every stream is falling back to an accrual twelfth, so the
 * guaranteed axis and the accrual axis are the same number and the toggle has
 * nothing to show. Worth saying out loud in the UI rather than rendering two
 * identical figures and letting the flip look broken.
 */
export const hasPayCalendar = (
  income: IncomeStreams,
  schedules: FundingSchedule[] = [],
): boolean => {
  const index = indexSchedules(schedules);
  return income.sources.some(s => {
    if (!s.isActive || isGrossStream(s.name)) return false;
    const sched = scheduleFor(index, s.fundingScheduleId, schedules);
    return !!sched && perCheckOf(s, schedules) > 0;
  });
};
